import { useParams, useNavigate, Link } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Users, Coins, Layers, Zap, Clock } from "lucide-react";
import { toast } from "sonner";

import { get, post } from "@/lib/api-client";
import { formatCurrency, formatDateTime, formatNumber } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Table, THead, TR, TH, TD } from "@/components/ui/Table";
import { Skeleton } from "@/components/ui/Skeleton";
import { StatusPill } from "@/components/ui/StatusPill";
import { Button } from "@/components/ui/Button";
import { StatsCard } from "@/features/customer/components/StatsCard";
import type { StarOffer } from "@/types/star";

interface OfferDetailResponse {
  offer: StarOffer;
  current_period: {
    id: number;
    period_start: string;
    period_end: string;
    total_pool_fund: string;
    total_entries_counted: number;
    is_closed: boolean;
    qualified_users_count: number;
  };
  // 🛠️ ADDED: To receive the list of achievers from the API
  claims: any[]; 
}

export function StarOfferDetailPage() {
  const { offerId } = useParams();
  const nav = useNavigate();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["admin-star-offer-detail", offerId],
    queryFn: () => get<OfferDetailResponse>(`/admin/star/offers/${offerId}`),
    enabled: !!offerId,
    refetchInterval: 10000, // Refresh every 10s to see live fund updates
  });

  const forceDistribute = useMutation({
    mutationFn: () => post(`/admin/star/offers/${offerId}/force-distribute`),
    onSuccess: () => {
      toast.success("Pool funds distributed successfully!");
      qc.invalidateQueries({ queryKey: ["admin-star-offer-detail", offerId] });
    },
  });

  if (isLoading) return <Skeleton className="h-96 w-full rounded-xl" />;
  if (!data) return null;

  const { offer, current_period: period, claims } = data; // 🛠️ Extracted claims
  const isPool = offer.reward_strategy === "pool";
  
  // Calculate estimated payout per user
  const fundNum = Number(period.total_pool_fund);
  const usersCount = period.qualified_users_count;
  const estimatedPayout = usersCount > 0 ? (fundNum / usersCount) : 0;

  return (
    <div className="space-y-6 animate-fade-in">
      <button onClick={() => nav(-1)} className="text-sm font-semibold text-surface-500 hover:text-surface-900 inline-flex items-center gap-1 transition-colors">
        <ArrowLeft className="h-4 w-4" /> Back to Offers
      </button>

      <header className="flex flex-wrap gap-4 justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-surface-900">{offer.name}</h1>
          <div className="mt-2 flex items-center gap-2">
            <StatusPill status={offer.status} />
            <StatusPill status={offer.period_type} />
            <span className="text-xs text-surface-500 font-medium">
              {isPool ? "Global Pool Strategy" : "Fixed Bonus Strategy"}
            </span>
          </div>
        </div>
        
        {isPool && !period.is_closed && (
          <Button 
            variant="warning" 
            leftIcon={<Zap className="h-4 w-4" />}
            loading={forceDistribute.isPending}
            onClick={() => {
              if (window.confirm("Are you sure you want to forcibly close this period and distribute funds now?")) {
                forceDistribute.mutate();
              }
            }}
          >
            Force Distribute Now
          </Button>
        )}
      </header>

      {/* Stats Cards Section */}
      {isPool ? (
        <>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            <StatsCard icon={Coins} tone="success" label="Total Pool Fund"
                        value={formatCurrency(period.total_pool_fund)}
                        hint="Added by new club entries" />
            <StatsCard icon={Users} tone="info" label="Qualified Users"
                        value={period.qualified_users_count}
                        hint="Users awaiting payout" />
            <StatsCard icon={Coins} tone="warning" label="Est. Payout / User"
                        value={formatCurrency(estimatedPayout)}
                        hint="Fund ÷ Qualified Users" />
            <StatsCard icon={Layers} tone="neutral" label="Entries Counted"
                        value={period.total_entries_counted}
                        hint={`@ ${formatCurrency(offer.contribution_per_entry)} per entry`} />
          </div>
          
          <Card>
            <CardHeader className="border-b border-surface-200">
              <CardTitle>Period Information</CardTitle>
            </CardHeader>
            <CardBody className="grid grid-cols-2 gap-4 text-sm bg-surface-50">
              <div>
                <span className="text-surface-500 block mb-1">Started At</span>
                <span className="font-semibold text-surface-900">{formatDateTime(period.period_start)}</span>
              </div>
              <div>
                <span className="text-surface-500 block mb-1">Ends At</span>
                <span className="font-semibold text-surface-900">{formatDateTime(period.period_end)}</span>
              </div>
              <div className="col-span-2">
                <span className="text-surface-500 block mb-1">Status</span>
                {period.is_closed ? (
                  <span className="text-success-600 font-bold bg-success-50 px-2 py-1 rounded">Closed & Distributed</span>
                ) : (
                  <span className="text-warning-600 font-bold bg-warning-50 px-2 py-1 rounded">Open (Accumulating Funds)</span>
                )}
              </div>
            </CardBody>
          </Card>
        </>
      ) : (
        <Card>
          <CardBody>
            <p className="text-surface-500">This is a Fixed Bonus offer. Payouts are made instantly upon qualification.</p>
          </CardBody>
        </Card>
      )}

      {/* 🛠️ NEW: Achievers List Table Section */}
      <Card>
        <CardHeader className="border-b border-surface-200 bg-white">
          <CardTitle>
            <span className="flex items-center gap-2">
              <Users className="w-5 h-5 text-surface-400" />
              Qualified Achievers ({claims ? claims.length : 0})
            </span>
          </CardTitle>
        </CardHeader>
        <CardBody className="p-0">
          {!claims || claims.length === 0 ? (
            <div className="py-16 text-center text-surface-500 flex flex-col items-center bg-surface-50/50">
              <Clock className="w-10 h-10 text-surface-300 mb-3" />
              <span className="font-semibold text-surface-700">No users have qualified yet.</span>
              <p className="text-sm mt-1">Users will appear here once they hit the target points.</p>
            </div>
          ) : (
            <div className="w-full overflow-x-auto">
              <Table className="min-w-[700px]">
                <THead>
                  <TR>
                    <TH>User ID</TH>
                    <TH>Full Name</TH>
                    <TH className="text-center">Points (Own + Team)</TH>
                    <TH className="text-right">Bonus Earned</TH>
                    <TH>Status</TH>
                    <TH>Qualified On</TH>
                  </TR>
                </THead>
                <tbody className="divide-y divide-surface-100 bg-white">
                  {claims.map((claim: any) => (
                    <TR key={claim.id} className="hover:bg-surface-50 transition-colors">
                      <TD>
                        <Link to={`/admin/users/${claim.user_id}`} className="font-mono text-xs font-bold text-brand-600 hover:text-brand-800">
                          {claim.user_id}
                        </Link>
                      </TD>
                      <TD className="font-semibold text-surface-900">{claim.full_name}</TD>
                      <TD className="text-center text-sm">
                        <span className="font-mono font-bold text-brand-700">{formatNumber(claim.combined_points / 100)}</span>
                        <div className="text-[10px] text-surface-500 mt-0.5 font-medium">({claim.own_points / 100} + {claim.team_points / 100})</div>
                      </TD>
                      <TD className="text-right font-bold text-success-700">
                        {Number(claim.bonus_amount) > 0 ? formatCurrency(claim.bonus_amount) : "—"}
                      </TD>
                      <TD><StatusPill status={claim.status} /></TD>
                      <TD className="text-[11px] font-medium text-surface-500 whitespace-nowrap">
                        {formatDateTime(claim.created_at)}
                      </TD>
                    </TR>
                  ))}
                </tbody>
              </Table>
            </div>
          )}
        </CardBody>
      </Card>
      
    </div>
  );
}